/**
 * API Helpers pour Believemy For Teams
 * Toutes les fonctions d'appel API pour la gestion des teams
 *
 * ⚠️ SÉCURITÉ : Ce fichier appelle uniquement les API routes Next.js (/api/teams/*)
 * Les API routes Next.js se chargent d'appeler le backend Symfony de manière sécurisée.
 * AUCUN secret (BELIEVEMY_SECRET_TOKEN) n'est exposé côté client.
 */

/**
 * Créer une nouvelle team avec abonnement Stripe
 * Utilise l'API route existante: /api/teams/create-checkout-session
 */
export async function createTeamCheckoutSession({ teamName, slug, seats }) {
    const response = await fetch("/api/teams/create-checkout-session", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            teamName,
            slug,
            seats,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message ||
                "Erreur lors de la création de la session checkout",
        );
    }

    return response.json();
}

/**
 * Obtenir les détails d'une team
 */
export async function getTeamDetails(teamId, userId) {
    const response = await fetch(`/api/teams/${teamId}/details`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de la récupération de la team",
        );
    }

    return response.json();
}

/**
 * Mettre à jour les informations d'une team
 */
export async function updateTeam(teamId, updates) {
    const response = await fetch(`/api/teams/${teamId}/update`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de la mise à jour");
    }

    return response.json();
}

/**
 * Inviter un membre dans la team
 */
export async function inviteMember(teamId, email, role = "member") {
    const response = await fetch(`/api/teams/${teamId}/invitations/create`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            email,
            role,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de l'invitation");
    }

    return response.json();
}

/**
 * Lister les membres d'une team
 */
export async function getTeamMembers(teamId) {
    const response = await fetch(`/api/teams/${teamId}/members`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de la récupération des membres",
        );
    }

    return response.json();
}

/**
 * Lister les invitations en cours d'une team
 */
export async function getTeamInvitations(teamId) {
    const response = await fetch(`/api/teams/${teamId}/invitations`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de la récupération des invitations",
        );
    }

    return response.json();
}

/**
 * Annuler/révoquer une invitation
 */
export async function cancelInvitation(teamId, invitationId) {
    const response = await fetch(
        `/api/teams/${teamId}/invitations/${invitationId}/cancel`,
        {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        },
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de l'annulation de l'invitation",
        );
    }

    return response.json();
}

/**
 * Suspendre un membre
 */
export async function suspendMember(teamId, memberId) {
    const response = await fetch(
        `/api/teams/${teamId}/members/${memberId}/suspend`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        },
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de la suspension");
    }

    return response.json();
}

/**
 * Réactiver un membre
 */
export async function reactivateMember(teamId, memberId) {
    const response = await fetch(
        `/api/teams/${teamId}/members/${memberId}/reactivate`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        },
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de la réactivation");
    }

    return response.json();
}

/**
 * Changer le rôle d'un membre
 */
export async function updateMemberRole(teamId, memberId, newRole) {
    const response = await fetch(
        `/api/teams/${teamId}/members/${memberId}/role`,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ role: newRole }),
        },
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors du changement de rôle");
    }

    return response.json();
}

/**
 * Retirer un membre de la team
 */
export async function removeMember(teamId, memberId) {
    const response = await fetch(
        `/api/teams/${teamId}/members/${memberId}/remove`,
        {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        },
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de la suppression");
    }

    return response.json();
}

/**
 * Accepter une invitation
 */
export async function acceptInvitation(invitationToken) {
    const response = await fetch("/api/teams/invitations/accept", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            invitationToken,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de l'acceptation");
    }

    return response.json();
}

/**
 * Décliner une invitation
 */
export async function declineInvitation(invitationToken) {
    const response = await fetch("/api/teams/invitations/decline", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            invitationToken,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors du refus");
    }

    return response.json();
}

/**
 * Obtenir l'activité complète d'une team (avec pagination et filtres)
 */
export async function getTeamActivity(teamId, userId, options = {}) {
    const { page = 1, limit = 20, timezone = "Europe/Paris", memberId, actionType, courseId } = options;

    const params = new URLSearchParams({
        userId,
        timezone,
        page: page.toString(),
        limit: limit.toString(),
    });

    if (memberId) params.append("memberId", memberId);
    if (actionType) params.append("actionType", actionType);
    if (courseId) params.append("courseId", courseId);

    const response = await fetch(
        `/api/teams/${teamId}/activity?${params.toString()}`,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de la récupération de l'activité"
        );
    }

    return response.json();
}

/**
 * Obtenir les analytics d'une team
 */
export async function getTeamAnalytics(
    teamId,
    userId,
    timezone = "Europe/Paris",
    period = "week",
) {
    const response = await fetch(
        `/api/teams/${teamId}/analytics?timezone=${timezone}&userId=${userId}&period=${period}`,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        },
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de la récupération des analytics",
        );
    }

    return response.json();
}

/**
 * Obtenir les analytics d'un membre spécifique
 */
export async function getMemberAnalytics(
    teamId,
    memberId,
    timezone = "Europe/Paris",
) {
    const response = await fetch(
        `/api/teams/${teamId}/members/${memberId}/analytics?timezone=${timezone}`,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        },
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de la récupération des analytics",
        );
    }

    return response.json();
}

/**
 * Assigner une formation à un membre
 */
export async function assignCourse(teamId, memberId, courseId, dueDate = null) {
    const response = await fetch(`/api/teams/${teamId}/assignments/create`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            memberId,
            courseId,
            dueDate,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de l'assignation");
    }

    return response.json();
}

/**
 * Obtenir les assignations d'une team
 */
export async function getTeamAssignments(teamId) {
    const response = await fetch(`/api/teams/${teamId}/assignments`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de la récupération des assignations",
        );
    }

    return response.json();
}

/**
 * Mettre à jour une assignation
 */
export async function updateAssignment(teamId, assignmentId, dueDate) {
    const response = await fetch(
        `/api/teams/${teamId}/assignments/${assignmentId}/update`,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ dueDate }),
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de la mise à jour");
    }

    return response.json();
}

/**
 * Supprimer une assignation
 */
export async function deleteAssignment(teamId, assignmentId) {
    const response = await fetch(
        `/api/teams/${teamId}/assignments/${assignmentId}/delete`,
        {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de la suppression");
    }

    return response.json();
}

/**
 * Ajouter des sièges à l'abonnement
 */
export async function addSeats(teamId, additionalSeats) {
    const response = await fetch(`/api/teams/${teamId}/add-seats`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            additionalSeats,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de l'ajout des places",
        );
    }

    return response.json();
}

/**
 * Réduire le nombre de sièges de l'abonnement
 */
export async function removeSeats(teamId, seatsToRemove) {
    const response = await fetch(`/api/teams/${teamId}/remove-seats`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            seatsToRemove,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.message || "Erreur lors de la réduction des places",
        );
    }

    return response.json();
}
